import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Gift } from "lucide-react";
import { toast } from "sonner";
import { http, login } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BraceletPreview } from "@/components/BraceletPreview";
import { useCharm } from "@/hooks/useCharm";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const PairCard = ({ pair, i, fandom, prefix, a, charm }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [gift, setGift] = useState(null);
  const showCharm = useCharm(charm);
  const id = slug(pair.map((c) => c.name).join(" "));
  const buy = async () => {
    const picked = showCharm();
    if (!user) { login(); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/designs/pair-checkout", {
        fandom, bracelets: pair.map((c) => ({ name: c.name, beads: c.beads })), origin_url: window.location.origin,
        charm_title: picked?.title ?? "", charm_text: picked?.text ?? "", gift_note: (gift ?? "").trim(),
      });
      if (!data?.checkout_url) throw new Error("no url");
      setTimeout(() => { window.location.href = data.checkout_url; }, charm ? 2500 : 0);
    } catch {
      toast.error("Couldn't open checkout. Please try again.");
      setBusy(false);
    }
  };
  return (
    <motion.article initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.08 }} data-testid={`${prefix}-${id}`}
      className={`rounded-[24px] border ${a.border} ${a.panel} p-5 sm:p-6 flex flex-col`}>
      <div className="grid grid-cols-2 gap-3">
        {pair.map((c) => <BraceletPreview key={c.name} beads={c.beads} className="max-w-[150px] mx-auto" />)}
      </div>
      <p className="mt-4 font-display text-2xl leading-tight text-center">
        {pair[0].name} <Heart className={`inline w-4 h-4 mx-1 ${a.text}`} /> {pair[1].name}
      </p>
      <p className={`mt-1 text-sm italic text-center ${a.soft}`}>Two handmade bracelets, one for each</p>
      {gift === null ? (
        <button onClick={() => setGift("")} data-testid={`${prefix}-gift-btn-${id}`}
          className={`mt-4 mx-auto inline-flex items-center gap-2 text-sm ${a.text} hover:underline`}>
          <Gift className="w-4 h-4" /> Add a gift note for a friend
        </button>
      ) : (
        <div className="mt-4">
          <textarea value={gift} onChange={(e) => setGift(e.target.value.slice(0, 200))} rows={3} autoFocus
            placeholder="To my best friend... (Liza will tuck this note in the package)" data-testid={`${prefix}-gift-input-${id}`}
            className="w-full rounded-xl border border-purple-200/25 bg-black/30 px-3 py-2 text-sm text-purple-50 placeholder:text-purple-200/60 focus:outline-none focus:border-amber-200/60" />
          <div className="flex justify-between text-xs text-purple-100/80 mt-1">
            <button onClick={() => setGift(null)} data-testid={`${prefix}-gift-remove-${id}`} className="hover:text-white">Remove note</button>
            <span>{gift.length}/200</span>
          </div>
        </div>
      )}
      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="font-mono text-xl text-amber-200" data-testid={`${prefix}-price-${id}`}>$20.00</span>
        <button onClick={buy} disabled={busy} data-testid={`${prefix}-buy-${id}`}
          className="rounded-full bg-purple-100 text-[#0e0616] px-5 py-2.5 text-sm font-medium hover:bg-amber-200 transition-colors disabled:opacity-60">
          {busy ? "Opening…" : user ? "Buy the set" : "Sign in to buy"}
        </button>
      </div>
    </motion.article>
  );
};

export const PairSets = ({ pairs, characters, fandom, prefix, accent: a, charm }) => {
  const byName = Object.fromEntries(characters.map((c) => [c.name, c]));
  const sets = pairs.map((p) => p.map((n) => byName[n])).filter((p) => p.every(Boolean));
  return (
    <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 mt-24" data-testid={prefix}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <h3 className="font-display text-4xl md:text-5xl leading-none">Pair <em className={a.text}>sets</em></h3>
        <p className="text-sm text-purple-100/85 max-w-sm">Two matching bracelets for a famous duo, $20 for the set. Wear one and give one away.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {sets.map((p, i) => <PairCard key={p[0].name + p[1].name} pair={p} i={i} fandom={fandom} prefix={prefix} a={a} charm={charm} />)}
      </div>
    </div>
  );
};
